// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {clsx} from 'clsx';
import _, {isNil, isObject} from 'lodash';
import React, {
  FC,
  // @ts-ignore We're actually on React 18 which includes the useDeferredValue hook
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {StickyTree, StickyTreeNode} from 'react-virtualized-sticky-tree';

import {useParts} from '../../../state/views/hooks';
import {runColor} from '../../../util/colors';
import {Grouping} from '../../../util/queryTypes';
import {getCustomRunName, groupedRunDisplayName} from '../../../util/runs';
import {TruncateTextTooltip} from '../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {SkeletonWrapper} from '../../Skeleton';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {DESIRED_ORDER} from './../constants';
import {useRunComparerContext} from './../RunComparerContextProvider';
import type {CollapsedTree, Tree, TreeLeaf} from './../types';
import {PanelRunComparerProps} from './../types';
import {getNewCollapsedTree} from './../utils/getNewCollapsedTree';
import {isBoringArray} from './../utils/isBoringArray';
import {isCollapsed} from './../utils/isCollapsed';
import {useRunComparerTree} from './../utils/useRunComparerTree';
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
} from './CellDimensionsSelector';
import {CellNew} from './CellNew';
import {CellTitleNew} from './CellTitleNew';

const HEADER_HEIGHT = 40;
const INNER_LEVEL_HEIGHT = 24;

const MAX_Z_INDEX = 1000;
const OVERSCAN_ROW_COUNT = 20;

type Node = TreeLeaf | Tree;
type StickyNode = {
  node: Node;
  id: string;
  nestingLevel: number;
  path: string[];
  collapsedTree?: CollapsedTree;
  CustomComponent?: () => JSX.Element;
  columnWidth: number;
  rowHeight: number;
};

type Meta = {
  toggle: (path: string[]) => void;
  tableWidth: number;
  isScrolling: boolean;
  renderedNodes: {overscanStartIndex: number; overscanStopIndex: number};
  treeRef: React.RefObject<StickyTree<StickyNode, Meta>> | null;
};

const renderSlave = (
  keyName: string,
  value: TreeLeaf,
  path: string[],
  columnWidth: number,
  rowHeight: number
) => {
  const depth = path.length;

  const hideRow = value.every(item => {
    if (isNil(item)) {
      return true;
    }
    if (isObject(item) && '_type' in item) {
      const hiddenTypes = ['images/separated', 'images', 'graph'];
      return hiddenTypes.includes(item._type);
    }

    return false;
  });

  if (hideRow) {
    return null;
  }

  return (
    <div
      className={clsx('slave-new', {
        diff: !isBoringArray(value),
      })}
      key={keyName}>
      <div
        className="header-cell-new"
        style={{paddingLeft: 26 + 12 * depth}}
        title={keyName}>
        <TruncateTextTooltip
          truncationType={TruncationType.Middle}
          className="text-middle"
          fullText={keyName}
          noLink={true}
        />
      </div>
      {value.map((item, i) => (
        <CellTitleNew
          item={item}
          key={i}
          columnWidth={columnWidth}
          rowHeight={rowHeight}
        />
      ))}
    </div>
  );
};

export const isValidRegex = (searchString: string | undefined) => {
  if (searchString == null) {
    return true;
  }
  try {
    new RegExp(searchString);
    return true;
  } catch (e) {
    return false;
  }
};

const sortKeys = (node: Tree) => (a: string, b: string) => {
  if (Array.isArray(node[a]) && !Array.isArray(node[b])) {
    return 1;
  }
  if (Array.isArray(node[b]) && !Array.isArray(node[a])) {
    return -1;
  }
  if (DESIRED_ORDER.includes(a) || DESIRED_ORDER.includes(b)) {
    return DESIRED_ORDER.indexOf(a) - DESIRED_ORDER.indexOf(b);
  }
  return a.localeCompare(b);
};

const getStickyTreeChildren =
  (
    node: Tree,
    path: string[],
    collapsedTree: CollapsedTree | undefined,
    nestingLevel: number,
    columnWidth: number,
    rowHeight: number
  ) =>
  (key: string) => ({
    node: {
      node: node[key],
      id: path.join('/') + '/' + key,
      nestingLevel: nestingLevel + 1,
      path: path.concat(key),
      collapsedTree,
      columnWidth,
      rowHeight,
    },
    height:
      nestingLevel + 1 === 0
        ? DEFAULT_ROW_HEIGHT
        : Array.isArray(node[key])
          ? rowHeight
          : INNER_LEVEL_HEIGHT,
    isSticky: !Array.isArray(node[key]),
    stickyTop:
      HEADER_HEIGHT +
      (nestingLevel + 1 === 0
        ? 0
        : DEFAULT_ROW_HEIGHT + INNER_LEVEL_HEIGHT * nestingLevel),
    zIndex: MAX_Z_INDEX - (nestingLevel + 2), // lower z index for deeper nodes so they don't scroll over their parents. add 2 since the minimum nesting level is -1.
  });

const getChildren = ({
  node,
  path,
  collapsedTree,
  nestingLevel,
  columnWidth,
  rowHeight,
}: StickyNode): StickyTreeNode<StickyNode>[] | undefined => {
  if (Array.isArray(node)) {
    return undefined;
  }
  if (isCollapsed(path, collapsedTree)) {
    return undefined;
  }
  return Object.keys(node)
    .sort(sortKeys(node))
    .map(
      getStickyTreeChildren(
        node,
        path,
        collapsedTree,
        nestingLevel,
        columnWidth,
        rowHeight
      )
    );
};

const rowRenderer = ({
  node: {
    node,
    id,
    nestingLevel,
    path,
    collapsedTree,
    CustomComponent,
    columnWidth,
    rowHeight,
  },
  style,
  meta = {
    toggle: () => {},
    tableWidth: 0,
    isScrolling: false,
    renderedNodes: {
      overscanStartIndex: 0,
      overscanStopIndex: OVERSCAN_ROW_COUNT,
    },
    treeRef: null,
  },
}: {
  node: StickyNode;
  style: React.CSSProperties;
  meta?: Meta;
}) => {
  const isLeaf = Array.isArray(node);
  const {
    toggle,
    tableWidth,
    isScrolling,
    renderedNodes: {overscanStartIndex, overscanStopIndex},
    treeRef,
  } = meta;
  let isOpen = !isCollapsed(path, collapsedTree);
  // Default histograms sections to closed
  if (path[0] === 'histograms' && isOpen) {
    isOpen = false;
  }
  const nodeIndex = treeRef?.current?.getNodeIndex(id);
  // If the user is currently scrolling, and the top of this row is outside of some buffer of the last fully rendered set of rows,
  // we are assuming that the row would not have rendered yet or been overscanned. So we show a loading indicator. This is a bit of
  // a hacky solution, but it's better than showing a blank section.
  if (
    isScrolling &&
    isLeaf &&
    nodeIndex != null &&
    (nodeIndex < overscanStartIndex || nodeIndex > overscanStopIndex)
  ) {
    return (
      <div
        style={{
          ...style,
          minWidth: tableWidth,
          width: '100%',
          justifyContent: 'center',
          height: rowHeight,
        }}
        key={id}>
        <SkeletonWrapper>
          <div
            className="ml-12 w-full rounded-lg bg-moon-250"
            style={{height: rowHeight - 24, minHeight: 12}}
          />
        </SkeletonWrapper>
      </div>
    );
  }

  return (
    <div
      style={{
        ...style,
        minWidth: tableWidth,
        width: '100%',
      }}
      key={id}>
      {CustomComponent != null ? (
        <CustomComponent />
      ) : isLeaf ? (
        renderSlave(path[path.length - 1], node, path, columnWidth, rowHeight)
      ) : (
        <div className="master-wrapper">
          <div
            onClick={() => toggle(path)}
            className={clsx('master-new', {
              'overlord-new': nestingLevel === 0,
              collapsed: !isOpen,
            })}>
            <div
              className="master-inner-new"
              style={{
                paddingLeft: nestingLevel === 0 ? 38 : 26 + 12 * nestingLevel,
              }}>
              <LegacyWBIcon name="next" />
              {path[path.length - 1]}
              {!isOpen && (
                <span className="collapsed-count-new">
                  {`(${Object.keys(node).length} collapsed)`}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const StickyTreeWrapper: FC<PanelRunComparerProps> = props => {
  const {
    config: {customRowHeight, customColumnWidth},
  } = props;
  const {localSearchString} = useRunComparerContext();
  const rowHeight = customRowHeight ?? DEFAULT_ROW_HEIGHT;
  const columnWidth = customColumnWidth ?? DEFAULT_COLUMN_WIDTH;

  const {tree} = useRunComparerTree({
    skip: !isValidRegex(localSearchString), // skip building the tree and checking for matches if the search string is not a valid regex
    loading: props.loading,
    runs: props.data.filtered,
    keyInfo: props.data.keyInfo,
    diffOnly: props.config.diffOnly,
    searchString: localSearchString, // use local saved search string not input search string to avoid re-constructing the tree on every keystroke
  });
  // Using useDeferredValue for the tree data prevents it from blocking the main thread when the tree is being rendered.
  // https://react.dev/reference/react/useDeferredValue#deferring-re-rendering-for-a-part-of-the-ui
  const fullTree = useDeferredValue(tree);

  const {runSetRefs} = usePanelsSharedRefsContext();
  const runSetParts = useParts(runSetRefs);
  const idToGroupingMap: {[key: string]: Grouping | undefined} = useMemo(() => {
    const map: {[key: string]: Grouping | undefined} = {};
    (props.pageQuery.runSets || []).forEach(runSet => {
      map[runSet.id] = runSet.grouping;
    });
    return map;
  }, [props.pageQuery.runSets]);
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const rootNode: StickyNode = useMemo(
    () => ({
      node: fullTree,
      id: 'root',
      nestingLevel: -1,
      path: [],
      collapsedTree: props.config.collapsedTree,
      columnWidth,
      rowHeight,
      CustomComponent: () => (
        <div className="header-slave-new">
          <div className="filler-cell-new" />
          {props.data.filtered.map(run => {
            const runSetConfig = runSetParts.find(
              rs => rs.id === run.runsetInfo.id
            );

            const runSetEntityName =
              runSetConfig?.project?.entityName ?? props.data.entityName;
            const runSetProjectName =
              runSetConfig?.project?.name ?? props.data.projectName;

            const groupKeys = idToGroupingMap[run.runsetInfo.id];
            const color = groupKeys
              ? runColor({
                  run,
                  groupKeys,
                  customRunColors: props.customRunColors,
                  colorPaletteOption,
                })
              : undefined;
            const displayName = groupKeys?.length
              ? groupedRunDisplayName(run, groupKeys)
              : (getCustomRunName(run.name, false, props.customRunNames) ??
                run.displayName);

            return (
              <CellNew
                displayName={displayName}
                columnWidth={columnWidth}
                key={run.id}
                run={run}
                color={color}
                disableRunLinks={props.disableRunLinks || false}
                runSetEntityName={runSetEntityName}
                runSetProjectName={runSetProjectName}
              />
            );
          })}
        </div>
      ),
    }),
    [
      fullTree,
      props,
      idToGroupingMap,
      runSetParts,
      columnWidth,
      rowHeight,
      colorPaletteOption,
    ]
  );
  const toggleCollapsed = (path: string[]) => {
    const newCollapsedTree = getNewCollapsedTree(
      path,
      props.config.collapsedTree
    );
    props.updateConfig({collapsedTree: newCollapsedTree});
  };
  const numRuns = props.data.filtered.length;
  const tableWidth = numRuns * columnWidth + 200;

  // This is used to show a loading indicator while we wait for the
  // row to render. The StickyTree component will overscan some rows to allow
  // for slow scrolling, but if a user scrolls really quickly, React can't keep up
  // in rendering the rows when virtualized, so it'll just show a blank section.
  // This gives a bit of a buffer to just show a simple loading row indicator instead of a blank section.
  const [isScrolling, setIsScrolling] = useState(true);
  const [renderedNodes, setRenderedNodes] = useState<{
    overscanStartIndex: number;
    overscanStopIndex: number;
  }>({overscanStartIndex: 0, overscanStopIndex: OVERSCAN_ROW_COUNT});
  const [loadingNodes, setLoadingNodes] = useState<{
    overscanStartIndex: number;
    overscanStopIndex: number;
  }>({overscanStartIndex: 0, overscanStopIndex: OVERSCAN_ROW_COUNT});
  const unsetIsScrolling = useMemo(
    () =>
      _.debounce(() => {
        setIsScrolling(false);
        setRenderedNodes(loadingNodes);
      }, 100), // 100ms to allow for the scrolling to finish. We can adjust this if rendering is still too slow.
    [setIsScrolling, setRenderedNodes, loadingNodes]
  );
  const treeRef = useRef<StickyTree<StickyNode, Meta>>(null);
  return (
    <AutoSizer>
      {({height, width}: {height: number; width: number}) => (
        <StickyTree<StickyNode, Meta>
          ref={treeRef}
          root={{
            node: rootNode,
            height: HEADER_HEIGHT,
            isSticky: true,
            zIndex: MAX_Z_INDEX,
            stickyTop: 0,
          }}
          width={width}
          height={height}
          getChildren={stickyNode => getChildren(stickyNode)}
          rowRenderer={rowRenderer}
          meta={{
            toggle: toggleCollapsed,
            tableWidth,
            isScrolling,
            renderedNodes,
            treeRef,
          }}
          renderRoot={true}
          overscanRowCount={OVERSCAN_ROW_COUNT}
          onScroll={() => {
            setIsScrolling(true);
            unsetIsScrolling();
          }}
          onRowsRendered={({overscanStartIndex, overscanStopIndex}) => {
            if (
              overscanStartIndex !== loadingNodes.overscanStartIndex ||
              overscanStopIndex !== loadingNodes.overscanStopIndex
            ) {
              setLoadingNodes({
                overscanStartIndex,
                overscanStopIndex,
              });
            }
          }}
        />
      )}
    </AutoSizer>
  );
};
